import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Factory, Users, ArrowRight, Sparkles } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function HowItWorksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clientSteps = t('how.client_steps', { returnObjects: true }) || [];
  const subSteps = t('how.sub_steps', { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="how-it-works-page">
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <button onClick={() => navigate(-1)} className="text-xs text-zinc-500 hover:text-amber-400 mb-6" data-testid="how-back-btn">
          {t('how.back')}
        </button>
        <div className="text-center mb-12">
          <div className="inline-flex w-16 h-16 rounded-sm bg-gradient-to-br from-amber-500 to-orange-500 items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-zinc-950" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('how.title')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Client */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold">{t('how.client_title')}</h2>
            </div>
            <p className="text-sm text-zinc-400 mb-5 leading-relaxed">{t('how.client_desc')}</p>
            <ol className="space-y-2.5 mb-6">
              {clientSteps.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`client-step-${i}`}>
                  <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-zinc-300">{s}</span>
                </li>
              ))}
            </ol>
            <Button onClick={() => navigate('/register')} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="how-cta-client">
              {t('how.cta_client')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Subcontractor */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <Factory className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="text-xl font-semibold">{t('how.sub_title')}</h2>
            </div>
            <p className="text-sm text-zinc-400 mb-5 leading-relaxed">{t('how.sub_desc')}</p>
            <ol className="space-y-2.5 mb-6">
              {subSteps.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`sub-step-${i}`}>
                  <span className="shrink-0 w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-zinc-300">{s}</span>
                </li>
              ))}
            </ol>
            <Button onClick={() => navigate('/register-subcontractor')} className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold" data-testid="how-cta-sub">
              {t('how.cta_sub')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        <div className="text-center">
          <button onClick={() => navigate('/login')} className="text-sm text-zinc-400 hover:text-amber-400 transition-colors" data-testid="how-cta-login">
            {t('how.cta_login')}
          </button>
        </div>
      </div>
    </div>
  );
}
